/**
 * useRecordingActivity — Keep HIPAA inactivity timer from firing during capture.
 *
 * HIPAA §164.312(a)(2)(iii) compliance is enforced by SecurityWrapper's 7-min
 * inactivity tracker. DOM events, API calls, and visibilitychange all reset
 * that tracker, but an active MediaRecorder was previously invisible to it —
 * a clinician recording a child and sitting perfectly still would be logged
 * out mid-capture and lose the recording (in-memory blobs die on unmount).
 *
 * This hook fires the existing `easi-api-activity` event on a 30s cadence
 * while `active === true`, plus once on each start/stop edge. SecurityWrapper's
 * handler treats that event as authoritative non-idle evidence.
 *
 * Intentional design notes:
 * - Reuses the existing `easi-api-activity` channel rather than adding a new
 *   event type — SecurityWrapper needs no changes.
 * - `tracker.recordApiActivity()` (the handler path) bypasses the warning-shown
 *   gate that DOM events respect. This is deliberate: a clinician watching a
 *   child cannot be expected to click a dialog, and the MediaRecorder is
 *   objective evidence of work in progress.
 * - 30s cadence is two orders of magnitude below the rate axios interceptors
 *   already fire this event (once per request + once per response). The
 *   handler is well-conditioned for this traffic.
 * - Browsers throttle setInterval on hidden tabs (Chrome floor ~1/min). The
 *   visibilitychange handler in SecurityWrapper covers tab-return; worst case
 *   during tab-hidden the throttled pulse still keeps us under the 7-min
 *   timeout.
 */

import { useEffect } from "react";

const PULSE_INTERVAL_MS = 30_000;
const EVENT_NAME = "easi-api-activity";

export function useRecordingActivity(active: boolean): void {
  useEffect(() => {
    if (!active || typeof window === "undefined") return;

    const fire = () => window.dispatchEvent(new CustomEvent(EVENT_NAME));
    fire();
    const intervalId = window.setInterval(fire, PULSE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      fire();
    };
  }, [active]);
}
